import { useState, MouseEvent, ChangeEvent, FormEvent } from 'react'
// @ts-ignore
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { getAuth } from 'firebase/auth'
import { FiFlag } from 'react-icons/fi'
import styled from 'styled-components'
import { REPORTS_ENDPOINT, REPORT_REASONS, MAX_DETAILS_LENGTH } from '@/util/reports'

type ReportTarget = 'post' | 'muse' | 'profile' | 'live' | 'message'

interface ReportButtonProps {
  targetType: ReportTarget
  targetId: string
  targetLabel?: string
  buttonText?: string
  compact?: boolean
  dark?: boolean
  className?: string
}

type SubmitState = 'idle' | 'sending' | 'sent'

const HEADINGS: Record<ReportTarget, string> = {
  post: 'Report this post',
  muse: 'Report this content',
  profile: 'Report this creator',
  live: 'Report this live stream',
  message: 'Report this message'
}

const readIdToken = async (): Promise<string | null> => {
  const currentUser = getAuth().currentUser
  return currentUser ? currentUser.getIdToken() : null
}

export default function ReportButton({
  targetType,
  targetId,
  targetLabel,
  buttonText = 'Report',
  compact = false,
  dark = false,
  className
}: ReportButtonProps) {
  const router = useRouter()
  const [showModal, setShowModal] = useState(false)
  const [reason, setReason] = useState('')
  const [details, setDetails] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [isSignedIn, setIsSignedIn] = useState(false)
  const [submitState, setSubmitState] = useState<SubmitState>('idle')
  const [error, setError] = useState('')

  const openModal = () => {
    setIsSignedIn(Boolean(getAuth().currentUser))
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    if (submitState === 'sent') {
      setReason('')
      setDetails('')
      setContactEmail('')
      setSubmitState('idle')
    }
    setError('')
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!reason) {
      setError('Pick a reason')
      return
    }

    setSubmitState('sending')
    setError('')

    try {
      const idToken = await readIdToken()
      const res = await fetch(REPORTS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
        },
        body: JSON.stringify({
          targetType,
          targetId,
          targetLabel,
          reason,
          details,
          contactEmail: isSignedIn ? '' : contactEmail,
          pagePath: router.asPath
        })
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        setError(body.error || 'We could not send your report. Please try again.')
        setSubmitState('idle')
        return
      }

      setSubmitState('sent')
    } catch (err) {
      setError('We could not send your report. Please check your connection and try again.')
      setSubmitState('idle')
    }
  }

  return (
    <>
      <TriggerButton
        type="button"
        onClick={openModal}
        className={className}
        $dark={dark}
        aria-label={HEADINGS[targetType]}
      >
        <FiFlag size={compact ? 11 : 14} />
        {buttonText && <span>{buttonText}</span>}
      </TriggerButton>

      {showModal && createPortal(
        <ModalOverlay onClick={closeModal}>
          <ModalContent
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-heading"
            onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          >
            <ModalHeader>
              <h2 id="report-heading">{HEADINGS[targetType]}</h2>
              <CloseButton type="button" onClick={closeModal} aria-label="Close">&times;</CloseButton>
            </ModalHeader>

            {submitState === 'sent' ? (
              <ModalBody>
                <p className="font-soehne-kraftig mb-2">Thanks, your report has been sent.</p>
                <p className="text-gray-600 mb-6">
                  Our team reviews every report. If this breaks our{' '}
                  <Link href="/acceptable-use-policy"><a className="underline">Acceptable Use Policy</a></Link>,
                  we will remove it.
                </p>
                <SubmitButton type="button" onClick={closeModal}>Done</SubmitButton>
              </ModalBody>
            ) : (
              <form onSubmit={handleSubmit}>
                <ModalBody>
                  <p className="text-gray-600 mb-4">
                    Tell us what is wrong. Reports are confidential and reviewed by our team.
                  </p>

                  <fieldset className="mb-4">
                    <legend className="block text-sm font-medium mb-2">Reason</legend>
                    {REPORT_REASONS.map((option: { value: string, label: string }) => (
                      <ReasonOption key={option.value} $selected={reason === option.value}>
                        <input
                          type="radio"
                          name="report-reason"
                          value={option.value}
                          checked={reason === option.value}
                          onChange={() => setReason(option.value)}
                        />
                        <span>{option.label}</span>
                      </ReasonOption>
                    ))}
                  </fieldset>

                  <label htmlFor="report-details" className="block text-sm font-medium mb-2">
                    Details {reason === 'other' ? '' : '(optional)'}
                  </label>
                  <TextArea
                    id="report-details"
                    rows={3}
                    maxLength={MAX_DETAILS_LENGTH}
                    value={details}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDetails(e.target.value)}
                    placeholder="Anything that helps us review it"
                  />

                  {!isSignedIn && (
                    <>
                      <label htmlFor="report-email" className="block text-sm font-medium mt-4 mb-2">
                        Your email (optional, so we can follow up)
                      </label>
                      <TextInput
                        id="report-email"
                        type="email"
                        value={contactEmail}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setContactEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </>
                  )}

                  {error && <ErrorText role="alert">{error}</ErrorText>}

                  <SubmitButton type="submit" disabled={submitState === 'sending'}>
                    {submitState === 'sending' ? 'Sending…' : 'Send report'}
                  </SubmitButton>
                </ModalBody>
              </form>
            )}
          </ModalContent>
        </ModalOverlay>,
        document.body
      )}
    </>
  )
}

const TriggerButton = styled.button<{ $dark: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  height: auto;
  cursor: pointer;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $dark }) => ($dark ? 'rgba(255, 255, 255, 0.6)' : '#6b7280')};
  transition: color 0.2s;

  &:hover {
    color: #c0392b;
  }
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`

const ModalContent = styled.div`
  background: white;
  color: #111111;
  border-radius: 12px;
  max-width: 480px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: left;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  color: #9ca3af;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;

  &:hover {
    color: #000;
  }
`

const ModalBody = styled.div`
  padding: 20px;
`

const ReasonOption = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  border: 2px solid ${({ $selected }) => ($selected ? '#000' : '#e5e7eb')};
  cursor: pointer;
  font-size: 14px;

  input {
    accent-color: #000;
  }
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #000;
  }
`

const TextInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #000;
  }
`

const ErrorText = styled.p`
  margin-top: 12px;
  color: #c0392b;
  font-size: 14px;
`

const SubmitButton = styled.button`
  width: 100%;
  margin-top: 16px;
  background: #000;
  color: #fff;
  padding: 14px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: background 0.2s;

  &:hover {
    background: #333;
  }

  &:disabled {
    background: #6b7280;
    cursor: default;
  }
`
